// Global quick-add modal: single <quick-add-modal> instance reused by every
// product card. Product data arrives per-card as a JSON payload
// (see snippets/quick-add-product-json.liquid); this file renders the modal
// content from that data, owns the pricing engine, and drives the real
// Shopify AJAX cart.
(function () {
  'use strict';

  if (window.customElements.get('quick-add-modal')) return;

  // Single source of truth for the magic numbers the pricing engine uses.
  // Free shipping threshold is NOT here - it comes from the theme's existing
  // settings.free_shipping_amounts via a data attribute (see quick-add-modal.liquid)
  // so there's only one place that number lives theme-wide.
  var CONFIG = {
    subscriptionDiscount: 0.15, // 15% off when "Subscribe & save" is on
    duoSubscriptionDiscount: 0.0588, // "Duo" (bundle) products get 5.88% off instead
    routineAddonDiscount: 0.15, // 15% off the paired product's addon price
    // Subscribe & Save only offered on these two variant titles for regular
    // products. "Duo" (bundle) products are always eligible regardless of
    // variant (they use duoSubscriptionDiscount instead of this list).
    subscriptionEligibleSizes: ['8.5 Fl. Oz.', '32 oz.']
  };

  function isDuoProduct(product) {
    return /duo/i.test(product.title);
  }

  // Percent for display text, e.g. 0.0588 -> "5.88", 0.15 -> "15" (no
  // trailing zeros/decimals for whole-number rates).
  function formatPercent(rate) {
    return Math.round(rate * 10000) / 100;
  }

  // --- Analytics (BRD Section 11: Analytics & Event Tracking) --------------
  // All events push to window.dataLayer for GTM to pick up - the theme never
  // calls GA4 directly. Fire-and-forget: a tracking failure must never break
  // the UI, so every push is wrapped in try/catch.

  function uuid() {
    if (window.crypto && window.crypto.randomUUID) return window.crypto.randomUUID();
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
      var r = (Math.random() * 16) | 0;
      var v = c === 'x' ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
  }

  function pushDataLayerEvent(eventName, params) {
    try {
      window.dataLayer = window.dataLayer || [];
      if (params && params.ecommerce) {
        window.dataLayer.push({ ecommerce: null });
      }
      window.dataLayer.push(Object.assign({ event: eventName }, params));
    } catch (err) {
      // Analytics is fire-and-forget - never let a tracking failure surface.
    }
  }

  // Merchant-set quick_add_badge is free text ("Travel", "Best Value", ...).
  // The BRD's variant_tag is a fixed enum - anything that isn't recognized
  // collapses to 'none' rather than leaking arbitrary merchant text.
  function normalizeVariantTag(badge) {
    if (!badge) return 'none';
    var normalized = String(badge).trim().toLowerCase().replace(/\s+/g, '_');
    return normalized === 'travel' || normalized === 'best_value' ? normalized : 'none';
  }

  function detectDeviceType() {
    return window.matchMedia('(max-width: 767px)').matches ? 'mobile' : 'desktop';
  }

  // source_surface identifies where the popup was opened from. A trigger can
  // opt in to an exact value via data-source-surface on itself or an
  // ancestor (e.g. a recommendation rail component); otherwise it's inferred
  // from the current page's URL.
  function detectSourceSurface(opener) {
    var explicit = opener && opener.closest && opener.closest('[data-source-surface]');
    if (explicit) return explicit.getAttribute('data-source-surface');
    var path = window.location.pathname;
    if (path === '/') return 'homepage_carousel';
    if (path.indexOf('/collections/') === 0) return 'collection';
    if (path.indexOf('/search') === 0) return 'search';
    return 'plp';
  }

  // Measures the browser's actual reserved scrollbar width (the classic
  // desktop kind that takes layout space) by forcing a scrollable test div,
  // rather than comparing window.innerWidth to document.documentElement's
  // clientWidth - that comparison is unreliable on mobile, where scrollbars
  // are an overlay that takes no layout space but the two can still diverge
  // for unrelated reasons, wrongly triggering the compensation below.
  // Cached since the browser's scrollbar width never changes mid-session.
  var cachedScrollbarWidth = null;
  function getScrollbarWidth() {
    if (cachedScrollbarWidth !== null) return cachedScrollbarWidth;
    var outer = document.createElement('div');
    outer.style.cssText = 'visibility:hidden;overflow:scroll;position:absolute;top:-9999px;width:100px;height:100px;';
    var inner = document.createElement('div');
    inner.style.width = '100%';
    outer.appendChild(inner);
    document.body.appendChild(outer);
    cachedScrollbarWidth = outer.offsetWidth - inner.offsetWidth;
    document.body.removeChild(outer);
    return cachedScrollbarWidth;
  }

  function isSubscribeEligible(product, variant) {
    if (!variant) return false;
    // Duo (bundle) products are always eligible, on every variant - they
    // just use a different (5.88%) discount rate, not the size gate below.
    if (isDuoProduct(product)) return true;
    return CONFIG.subscriptionEligibleSizes.indexOf(variant.title) !== -1;
  }

  var DEFAULT_FREQUENCIES = [
    { id: '', name: 'Every 2 weeks' },
    { id: '', name: 'Every 4 weeks' },
    { id: '', name: 'Every 6 weeks' },
    { id: '', name: 'Every 8 weeks' }
  ];

  function formatMoney(cents, format) {
    if (typeof cents !== 'number') cents = parseFloat(cents) || 0;
    var formatString = format || (window.theme && window.theme.settings && window.theme.settings.moneyWithCurrencyFormat) || '${{amount}}';
    var placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;

    function withDelimiters(number, precision, thousands, decimal) {
      precision = precision || 2;
      thousands = thousands || ',';
      decimal = decimal || '.';
      if (isNaN(number) || number === null) return '0.00';
      number = (number / 100).toFixed(precision);
      var parts = number.split('.');
      var dollars = parts[0].replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1' + thousands);
      var centsPart = parts[1] ? decimal + parts[1] : '';
      return dollars + centsPart;
    }

    var match = formatString.match(placeholderRegex);
    if (!match) return formatString;

    var value;
    switch (match[1]) {
      case 'amount_no_decimals':
        value = withDelimiters(cents, 0);
        break;
      case 'amount_with_comma_separator':
        value = withDelimiters(cents, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = withDelimiters(cents, 0, '.', ',');
        break;
      case 'amount_with_apostrophe_separator':
        value = withDelimiters(cents, 2, "'", '.');
        break;
      default:
        value = withDelimiters(cents, 2);
    }
    return formatString.replace(placeholderRegex, value);
  }

  function updateCartCountBadge(count) {
    var selectors = ['.cart-link__count', '.cart-drawer__title-count'];
    selectors.forEach(function (selector) {
      document.querySelectorAll(selector).forEach(function (el) {
        el.textContent = count;
        el.setAttribute('data-cart-count', count);
        if (count > 0) {
          el.classList.remove('hidden');
          el.removeAttribute('aria-hidden');
        } else {
          el.classList.add('hidden');
          el.setAttribute('aria-hidden', 'true');
        }
      });
    });
  }

  function openCartDrawer() {
    var cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer) {
      document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open'));
    }
  }

  function refreshCartDrawer() {
    var cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer) {
      document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh'));
    }
  }

  // Finds the variant whose options match `selected` (an array of values,
  // positional to match product.options ordering).
  function findVariant(product, selected) {
    return product.variants.find(function (variant) {
      return variant.options.every(function (value, index) {
        return value === selected[index];
      });
    });
  }

  // For a given option index/value, is there any real, available variant
  // combination using it together with the OTHER currently selected values?
  function isValueAvailable(product, selected, optionIndex, value) {
    var probe = selected.slice();
    probe[optionIndex] = value;
    var variant = findVariant(product, probe);
    return !!variant && variant.available;
  }

  // Looks for a same-value variant (e.g. matching size) on the paired
  // product referenced by the quick_add_routine_pair metafield.
  function findPairedVariant(product, currentVariant) {
    var pair = product.routinePair;
    if (!pair) return null;
    return pair.variants.find(function (pairedVariant) {
      return pairedVariant.options.some(function (value) {
        return currentVariant.options.indexOf(value) !== -1;
      });
    }) || null;
  }

  function computePricing(product, variant, state) {
    var basePrice = variant.price;
    var routineAddon = 0;
    if (product.routinePair && state.routineAddon) {
      var pairedVariant = findPairedVariant(product, variant);
      if (pairedVariant) {
        routineAddon = Math.round(pairedVariant.price * (1 - CONFIG.routineAddonDiscount));
      }
    }
    var unitPrice = basePrice + routineAddon;
    var subTotal = unitPrice * state.quantity;
    var discountRate = isDuoProduct(product) ? CONFIG.duoSubscriptionDiscount : CONFIG.subscriptionDiscount;
    var ctaTotal = state.subscribe ? Math.round(subTotal * (1 - discountRate)) : subTotal;
    return { basePrice: basePrice, routineAddon: routineAddon, unitPrice: unitPrice, subTotal: subTotal, ctaTotal: ctaTotal, discountRate: discountRate };
  }

  function maxQuantityFor(variant) {
    if (variant.inventoryManagement && variant.inventoryPolicy === 'deny') {
      return Math.max(variant.inventoryQuantity, 0);
    }
    return Infinity;
  }

  // --- Chip pill badges ("Travel" / "Best value" / anything else) ----------
  // Fully merchant-controlled via the variant.metafields.custom.quick_add_badge
  // text metafield (per 2026-09-22 decision) - no computed logic. Whichever
  // variant matches a given option value is used as that chip's badge source.

  function findVariantForOptionValue(product, optionIndex, value) {
    var matches = product.variants.filter(function (v) { return v.options[optionIndex] === value; });
    return matches.find(function (v) { return v.available; }) || matches[0] || null;
  }

  var FOCUSABLE = 'a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])';

  class QuickAddModalElement extends HTMLElement {
    connectedCallback() {
      this.overlay = this.querySelector('[data-qam-overlay]');
      this.card = this.querySelector('[data-qam-card]');
      this.closeBtn = this.querySelector('[data-qam-close]');
      this.scroll = this.querySelector('[data-qam-scroll]');
      this.headerEl = this.querySelector('[data-qam-header]');
      this.groupsEl = this.querySelector('[data-qam-groups]');
      this.qtyEl = this.querySelector('[data-qam-qty]');
      this.shipEl = this.querySelector('[data-qam-ship]');
      this.footerEl = this.querySelector('[data-qam-footer]');
      this.errorEl = this.querySelector('[data-qam-error]');
      this.toast = document.querySelector('[data-qam-toast]');
      this.toastText = this.toast && this.toast.querySelector('[data-qam-toast-text]');

      this.moneyFormat = this.dataset.moneyFormat || '${{amount}}';
      this.freeShippingThreshold = parseInt(this.dataset.freeShippingThreshold, 10) || 7500;
      this.sourceTemplate = this.dataset.sourceTemplate || '';
      this.currency = this.dataset.currency || 'USD';

      this.isOpen = false;
      this.isSubmitting = false;
      this.cartSubtotal = 0;

      var self = this;
      this._onOverlayClick = function () { self.close('overlay_click'); };
      this._onCloseClick = function () { self.close('close_button'); };
      this._onKeydown = this.handleKeydown.bind(this);
      this._onDocClick = this.handleTriggerClick.bind(this);
      this._onOpenEvent = this.handleOpenEvent.bind(this);

      this.overlay.addEventListener('click', this._onOverlayClick);
      this.closeBtn.addEventListener('click', this._onCloseClick);
      document.addEventListener('click', this._onDocClick);
      document.addEventListener('dispatch:quick-add-modal:open', this._onOpenEvent);
    }

    disconnectedCallback() {
      document.removeEventListener('click', this._onDocClick);
      document.removeEventListener('dispatch:quick-add-modal:open', this._onOpenEvent);
    }

    handleTriggerClick(evt) {
      var trigger = evt.target.closest('[data-quick-add-trigger]');
      if (!trigger || trigger.disabled) return;
      var host = trigger.closest('.variant-slider-container') || trigger.closest('product-block');
      var jsonEl = host && host.querySelector('[data-quick-add-product-json]');
      if (!jsonEl) return;
      evt.preventDefault();
      var product;
      try {
        product = JSON.parse(jsonEl.textContent);
      } catch (err) {
        console.error('Quick add modal: invalid product JSON', err);
        return;
      }
      this.open(product, trigger);
    }

    handleOpenEvent(evt) {
      if (!evt.detail || !evt.detail.product) return;
      this.open(evt.detail.product, evt.detail.opener);
    }

    async open(product, opener) {
      clearTimeout(this._closeTimer);
      this.product = product;
      this.opener = opener || null;
      this.resetState();

      // Analytics session: one UUID per open, reused by every event until
      // this popup closes (BRD 11.1/11.2). Set before render() so the
      // shipping-bar baseline (see renderShipping) is established silently
      // instead of firing a spurious transition on first paint.
      this.popupSessionId = uuid();
      this.sourceSurface = detectSourceSurface(this.opener);
      this.deviceType = detectDeviceType();
      this._openedAt = Date.now();

      // Cart-aware free shipping calc needs the existing cart subtotal.
      try {
        var res = await fetch('/cart.js');
        var cart = await res.json();
        this.cartSubtotal = cart.total_price;
      } catch (err) {
        this.cartSubtotal = 0;
      }

      this.render();
      this.trackOpen();

      this.hidden = false;
      this.setAttribute('data-open', 'true');
      // Compensate for the scrollbar that 'qam-open' below is about to hide,
      // so the page's content doesn't jump sideways when it disappears.
      // Only when the page actually has one right now (a short page with no
      // vertical overflow has nothing to compensate for) AND the browser's
      // scrollbar takes real layout space (0 on mobile/overlay-scrollbar
      // browsers) - otherwise this would add padding nothing needs.
      var pageHasScrollbar = document.documentElement.scrollHeight > document.documentElement.clientHeight;
      document.body.style.paddingRight = (pageHasScrollbar ? getScrollbarWidth() : 0) + 'px';
      document.body.classList.add('qam-open');
      this.isOpen = true;
      document.addEventListener('keydown', this._onKeydown);

      requestAnimationFrame(function () {
        this.classList.add('is-on');
      }.bind(this));

      var self = this;
      requestAnimationFrame(function () {
        var firstFocusable = self.card.querySelector(FOCUSABLE);
        if (firstFocusable) firstFocusable.focus();
      });
    }

    close(reason) {
      if (!this.isOpen) return;
      this.isOpen = false;
      this.trackClose(reason || 'close_button');
      this.classList.remove('is-on');
      document.body.classList.remove('qam-open');
      document.body.style.paddingRight = '';
      document.removeEventListener('keydown', this._onKeydown);

      var self = this;
      this._closeTimer = setTimeout(function () {
        self.hidden = true;
        self.removeAttribute('data-open');
      }, 220);

      if (this.opener && typeof this.opener.focus === 'function') {
        this.opener.focus();
      }
    }

    handleKeydown(evt) {
      if (evt.key === 'Escape') {
        this.close('escape_key');
        return;
      }
      if (evt.key !== 'Tab') return;
      var focusable = Array.prototype.slice.call(this.card.querySelectorAll(FOCUSABLE));
      if (!focusable.length) return;
      var first = focusable[0];
      var last = focusable[focusable.length - 1];
      if (evt.shiftKey && document.activeElement === first) {
        evt.preventDefault();
        last.focus();
      } else if (!evt.shiftKey && document.activeElement === last) {
        evt.preventDefault();
        first.focus();
      }
    }

    resetState() {
      var product = this.product;

      var defaultVariant = null;

      // 1) Highest priority: a variant explicitly flagged eligible_variant = true
      //    (merchant override - wins over everything else below).
      //    quick_add_badge is display-only and never affects this pick.
      defaultVariant = product.variants.find(function (v) { return v.eligible === true && v.available; });

      // 2) Otherwise, the product's own configured default (retail) variant.
      if (!defaultVariant) {
        defaultVariant = product.variants.find(function (v) { return v.id === product.defaultVariantId; });
      }

      // 3) If that's missing or out of stock, fall back to the next
      //    available variant, then just the first one.
      if (!defaultVariant || !defaultVariant.available) {
        defaultVariant = product.variants.find(function (v) { return v.available; }) || defaultVariant || product.variants[0];
      }

      var hasRealSellingPlans = product.sellingPlanGroups[0] && product.sellingPlanGroups[0].sellingPlans.length;
      var frequencies = hasRealSellingPlans ? product.sellingPlanGroups[0].sellingPlans : DEFAULT_FREQUENCIES;
      // "Every 4 weeks" is index 1 in the static fallback list; with real
      // merchant-configured selling plans we have no way to know which one
      // is "the default", so fall back to the first plan.
      var defaultFrequencyIndex = hasRealSellingPlans ? 0 : 1;

      this.state = {
        selected: defaultVariant.options.slice(),
        quantity: 1,
        subscribe: true,
        frequencyIndex: defaultFrequencyIndex,
        routineAddon: false
      };
      this.frequencies = frequencies;
      this.errorEl.hidden = true;
      this.isSubmitting = false;
      // Forces the first updatePricing() call after open to treat the
      // default variant's eligibility as a fresh transition.
      this._lastSubEligible = null;

      // Analytics counters (BRD 11) - reset for every fresh open() so a
      // reused <quick-add-modal> instance never leaks state between popups.
      this._interactionCount = 0;
      this._variantChangeIndex = 0;
      this._addAttempts = 0;
      this._addedToCart = false;
      this._qtyBaseline = null;
      this._lastShippingUnlocked = null;
      this._shippingChangeTrigger = 'variant_change';
    }

    currentVariant() {
      return findVariant(this.product, this.state.selected) || this.product.variants[0];
    }

    render() {
      this.renderHeader();
      this.renderGroups();
      this.renderQuantity();
      this.renderFooter();
      this.updatePricing();
    }

    renderHeader() {
      var product = this.product;
      this.headerEl.innerHTML =
        '<a class="qam__thumb" href="' + product.url + '" data-qam-view-details="product_image"><img src="' + product.image + '" alt="" width="64" height="64" loading="eager"></a>' +
        '<div class="qam__hd-text">' +
          '<h3 class="qam__name"><a href="' + product.url + '" data-qam-view-details="product_title">' + escapeHtml(product.title) + '</a></h3>' +
          '<div class="qam__rating product-block__rating">' + (product.ratingHtml || '') + '</div>' +
          '<div class="qam__price-wrap product-price-display">' +
            '<span class="qam__price price-current" data-qam-price></span>' +
            '<span class="price-compare" data-qam-price-compare hidden></span>' +
          '</div>' +
        '</div>';

      var self = this;
      this.headerEl.querySelectorAll('[data-qam-view-details]').forEach(function (link) {
        link.addEventListener('click', function () {
          self.trackViewDetailsClick(link.getAttribute('data-qam-view-details'));
        });
      });
    }

    renderGroups() {
      var product = this.product;
      var state = this.state;
      var html = '';

      product.options.forEach(function (option, optionIndex) {
        if (option.values.length <= 1) return; // nothing to choose - hide the group

        var selectedValue = state.selected[optionIndex];
        html += '<div class="qam__group" role="radiogroup" aria-label="' + escapeHtml(option.name) + '">';
        html += '<div class="qam__group-label"><em>' + escapeHtml(option.name.toUpperCase()) + '</em><span class="qam__group-note">' + escapeHtml(selectedValue) + ' selected</span></div>';
        html += '<div class="qam__chips">';
        option.values.forEach(function (value) {
          var available = isValueAvailable(product, state.selected, optionIndex, value);
          var selected = value === selectedValue;
          var repVariant = findVariantForOptionValue(product, optionIndex, value);
          var badge = repVariant && repVariant.badge;
          html += '<button type="button" class="qam__chip' + (selected ? ' is-on' : '') + (available ? '' : ' is-disabled') +
            '" role="radio" aria-checked="' + selected + '"' + (available ? '' : ' disabled aria-disabled="true"') +
            ' data-qam-chip data-option-index="' + optionIndex + '" data-value="' + escapeHtml(value) + '">' +
            '<span>' + escapeHtml(value) + '</span>' +
            (badge ? '<small>' + escapeHtml(badge) + '</small>' : '') +
            '</button>';
        });
        html += '</div></div>';
      });

      if (product.routinePair) {
        var addonSelected = state.routineAddon;
        html += '<div class="qam__group" role="radiogroup" aria-label="Routine">';
        html += '<div class="qam__group-label"><em>ROUTINE</em><span class="qam__group-note">' + (addonSelected ? escapeHtml('+ ' + product.routinePair.title) : (escapeHtml(product.title) + ' only')) + ' selected</span></div>';
        html += '<div class="qam__chips">';
        html += '<button type="button" class="qam__chip' + (!addonSelected ? ' is-on' : '') + '" role="radio" aria-checked="' + (!addonSelected) + '" data-qam-routine="false"><span>' + escapeHtml(product.title) + ' only</span></button>';
        html += '<button type="button" class="qam__chip' + (addonSelected ? ' is-on' : '') + '" role="radio" aria-checked="' + addonSelected + '" data-qam-routine="true"><span>+ ' + escapeHtml(product.routinePair.title) + '</span><small>Save ' + Math.round(CONFIG.routineAddonDiscount * 100) + '%</small></button>';
        html += '</div></div>';
      }

      this.groupsEl.innerHTML = html;

      var self = this;
      this.groupsEl.querySelectorAll('[data-qam-chip]').forEach(function (chip) {
        chip.addEventListener('click', function () {
          if (chip.disabled) return;
          var optionIndex = parseInt(chip.dataset.optionIndex, 10);
          var newValue = chip.dataset.value;
          if (self.state.selected[optionIndex] === newValue) return;
          var previousVariant = self.currentVariant();
          self.state.selected[optionIndex] = newValue;
          self.trackVariantChange(previousVariant, self.currentVariant());
          self._shippingChangeTrigger = 'variant_change';
          self.renderGroups();
          self.renderQuantity();
          self.updatePricing();
        });
      });
      this.groupsEl.querySelectorAll('[data-qam-routine]').forEach(function (btn) {
        btn.addEventListener('click', function () {
          self.state.routineAddon = btn.dataset.qamRoutine === 'true';
          self._shippingChangeTrigger = 'variant_change';
          self.renderGroups();
          self.updatePricing();
        });
      });
    }

    renderQuantity() {
      var variant = this.currentVariant();
      var max = maxQuantityFor(variant);
      var state = this.state;
      if (state.quantity > max) state.quantity = Math.max(max, 1);

      this.qtyEl.innerHTML =
        '<span class="qam__qty-label">QUANTITY</span>' +
        '<div class="qam__stepper">' +
          '<button type="button" class="qam__step qam__step--minus" data-qam-qty-minus aria-label="Decrease quantity"' + (state.quantity <= 1 ? ' disabled' : '') + '>&minus;</button>' +
          '<span class="qam__qty-value">' + state.quantity + '</span>' +
          '<button type="button" class="qam__step qam__step--plus" data-qam-qty-plus aria-label="Increase quantity"' + (state.quantity >= max ? ' disabled' : '') + '>+</button>' +
        '</div>';

      var self = this;
      var minusBtn = this.qtyEl.querySelector('[data-qam-qty-minus]');
      var plusBtn = this.qtyEl.querySelector('[data-qam-qty-plus]');
      minusBtn.addEventListener('click', function () {
        if (self.state.quantity <= 1) return;
        if (self._qtyBaseline === null) self._qtyBaseline = self.state.quantity;
        self.state.quantity -= 1;
        self._shippingChangeTrigger = 'quantity_change';
        self.renderQuantity();
        self.updatePricing();
        self.trackQuantityChangeDebounced('decrease');
      });
      plusBtn.addEventListener('click', function () {
        if (self.state.quantity >= max) return;
        if (self._qtyBaseline === null) self._qtyBaseline = self.state.quantity;
        self.state.quantity += 1;
        self._shippingChangeTrigger = 'quantity_change';
        self.renderQuantity();
        self.updatePricing();
        self.trackQuantityChangeDebounced('increase');
      });
    }

    renderFooter() {
      var self = this;
      var frequencies = this.frequencies;
      var optionsHtml = frequencies.map(function (freq, index) {
        return '<option value="' + index + '"' + (index === self.state.frequencyIndex ? ' selected' : '') + '>' + escapeHtml(freq.name) + '</option>';
      }).join('');

      this.footerEl.innerHTML =
        '<div class="qam__sub">' +
          '<button type="button" class="qam__switch" role="switch" aria-checked="' + this.state.subscribe + '" data-qam-switch></button>' +
          '<span class="qam__subtext" data-qam-subtext></span>' +
          '<select class="qam__freq" data-qam-freq aria-label="Delivery frequency"' + (this.state.subscribe ? '' : ' disabled') + '>' + optionsHtml + '</select>' +
        '</div>' +
        '<button type="button" class="qam__submit" data-qam-submit>' +
          '<span>ADD TO BAG</span><span data-qam-submit-total></span>' +
        '</button>' +
        '<a class="qam__details" href="' + this.product.url + '" data-qam-view-details="view_details_link">View full details &rarr;</a>';

      this.footerEl.querySelector('[data-qam-switch]').addEventListener('click', function () {
        var variant = self.currentVariant();
        var beforePricing = computePricing(self.product, variant, self.state);
        self.state.subscribe = !self.state.subscribe;
        var afterPricing = computePricing(self.product, variant, self.state);
        self.trackSubscriptionToggle(beforePricing, afterPricing);
        self._shippingChangeTrigger = 'subscription_toggle';
        self.renderFooter();
        self.updatePricing();
      });
      this.footerEl.querySelector('[data-qam-freq]').addEventListener('change', function (evt) {
        var previousFrequency = self.frequencies[self.state.frequencyIndex];
        self.state.frequencyIndex = parseInt(evt.target.value, 10);
        var newFrequency = self.frequencies[self.state.frequencyIndex];
        evt.target.classList.add('is-active');
        self.trackFrequencyChange(previousFrequency, newFrequency);
      });
      this.footerEl.querySelector('[data-qam-freq]').addEventListener('blur', function (evt) {
        evt.target.classList.remove('is-active');
      });
      this.footerEl.querySelector('[data-qam-submit]').addEventListener('click', function () {
        self.submit();
      });
      this.footerEl.querySelector('[data-qam-view-details]').addEventListener('click', function () {
        self.trackViewDetailsClick('view_details_link');
      });
    }

    updatePricing() {
      var variant = this.currentVariant();

      var subEl = this.footerEl.querySelector('.qam__sub');
      var subEligible = isSubscribeEligible(this.product, variant);
      if (subEl) {
        // Whenever the selected variant isn't Subscribe & Save eligible,
        // remove the block entirely (no reserved space) so the popup's
        // height shrinks to fit - it only reappears for an eligible variant.
        subEl.hidden = !subEligible;
      }
      // Only flip the subscribe toggle when eligibility actually changes
      // (a real variant switch) - not on every re-render (qty/routine
      // changes), so a manual off-toggle on an eligible variant sticks.
      if (subEligible !== this._lastSubEligible) {
        this.state.subscribe = subEligible;
        this._lastSubEligible = subEligible;
      }

      var pricing = computePricing(this.product, variant, this.state);

      var priceEl = this.headerEl.querySelector('[data-qam-price]');
      if (priceEl) priceEl.textContent = formatMoney(pricing.basePrice, this.moneyFormat);

      var compareEl = this.headerEl.querySelector('[data-qam-price-compare]');
      if (compareEl) {
        if (variant.compareAtPrice > variant.price) {
          compareEl.textContent = formatMoney(variant.compareAtPrice, this.moneyFormat);
          compareEl.hidden = false;
        } else {
          compareEl.hidden = true;
        }
      }

      var subtextEl = this.footerEl.querySelector('[data-qam-subtext]');
      var switchEl = this.footerEl.querySelector('[data-qam-switch]');
      var freqEl = this.footerEl.querySelector('[data-qam-freq]');
      if (subtextEl && switchEl) {
        switchEl.setAttribute('aria-checked', this.state.subscribe);
        switchEl.classList.toggle('is-on', this.state.subscribe);
        subtextEl.innerHTML = this.state.subscribe
          ? '<b>Subscribed.</b> Saving ' + formatPercent(pricing.discountRate) + '%'
          : 'Subscribe &amp; save ' + formatPercent(pricing.discountRate) + '%';
      }
      if (freqEl) freqEl.disabled = !this.state.subscribe;

      var totalEl = this.footerEl.querySelector('[data-qam-submit-total]');
      if (totalEl) totalEl.textContent = formatMoney(pricing.ctaTotal, this.moneyFormat);

      var submitBtn = this.footerEl.querySelector('[data-qam-submit]');
      if (submitBtn) submitBtn.disabled = !variant.available || this.isSubmitting;

      this.renderShipping(pricing.ctaTotal);
    }

    renderShipping(lineTotalCents) {
      var projected = this.cartSubtotal + lineTotalCents;
      var threshold = this.freeShippingThreshold;
      var progress = Math.min(100, (projected / threshold) * 100);
      var unlocked = projected >= threshold;

      this.shipEl.innerHTML =
        '<p>' + (unlocked
          ? '<b>Free shipping unlocked.</b>'
          : "You're <b>" + formatMoney(threshold - projected, this.moneyFormat) + '</b> away from free shipping') +
        '</p>' +
        '<div class="qam__ship-bar"><i style="width:' + progress + '%"></i></div>';

      // Fires only on the locked<->unlocked transition, never on every
      // recalculation (BRD event 6). _lastShippingUnlocked starts null on
      // open() so the very first render establishes a silent baseline.
      if (this._lastShippingUnlocked !== null && this._lastShippingUnlocked !== unlocked) {
        this.trackShippingStateChange(unlocked);
      }
      this._lastShippingUnlocked = unlocked;
    }

    async submit() {
      if (this.isSubmitting) return;
      var variant = this.currentVariant();
      if (!variant.available) return;

      this.isSubmitting = true;
      var submitBtn = this.footerEl.querySelector('[data-qam-submit]');
      submitBtn.classList.add('is-loading');
      submitBtn.disabled = true;
      this.errorEl.hidden = true;

      var properties = {};
      var routineLabel = null;
      if (this.product.routinePair && this.state.routineAddon) {
        routineLabel = '+ ' + this.product.routinePair.title;
        properties['Routine'] = routineLabel;
      }
      var frequency = this.frequencies[this.state.frequencyIndex];
      // When a real selling_plan is attached below, Shopify already shows its
      // name natively via item.selling_plan_allocation.selling_plan.name in
      // the cart drawer - adding a "Frequency" property too would duplicate
      // it. Only tag the property for the static fallback (no real plan).
      if (this.state.subscribe && !frequency.id) {
        properties['Frequency'] = frequency.name;
      }

      var body = {
        id: variant.id,
        quantity: this.state.quantity
      };
      if (Object.keys(properties).length) body.properties = properties;
      // Only attach a real selling_plan when the product actually has one -
      // that's the one part of this flow Shopify can natively discount.
      if (this.state.subscribe && frequency.id) body.selling_plan = frequency.id;

      try {
        var res = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(body)
        });
        var data = await res.json();
        if (!res.ok) {
          throw new Error((data && data.description) || 'Could not add this item to your bag.');
        }

        this._addedToCart = true;
        this.trackAddToCart(variant, frequency);

        var cartRes = await fetch('/cart.js');
        var cart = await cartRes.json();
        updateCartCountBadge(cart.item_count);
        refreshCartDrawer();

        this.close('add_success');
        openCartDrawer();
        this.showToast(this.product.title + ' added to bag');
      } catch (err) {
        this.trackAddError(err);
        this.errorEl.textContent = err.message || 'Something went wrong. Please try again.';
        this.errorEl.hidden = false;
      } finally {
        this.isSubmitting = false;
        submitBtn.classList.remove('is-loading');
        submitBtn.disabled = !variant.available;
      }
    }

    showToast(message) {
      if (!this.toast) return;
      clearTimeout(this._toastTimer);
      this.toastText.textContent = message;
      this.toast.hidden = false;
      requestAnimationFrame(function () {
        this.toast.classList.add('is-on');
      }.bind(this));
      this._toastTimer = setTimeout(function () {
        this.toast.classList.remove('is-on');
        setTimeout(function () { this.toast.hidden = true; }.bind(this), 320);
      }.bind(this), 2500);
    }

    // --- Analytics (BRD Section 11.2 shared context + 11.3 event catalogue) --

    buildAnalyticsContext() {
      var product = this.product;
      var variant = this.currentVariant();
      var pricing = computePricing(product, variant, this.state);
      var frequency = this.frequencies[this.state.frequencyIndex];
      var quantity = this.state.quantity || 1;
      var effectiveUnitPrice = pricing.ctaTotal / quantity;
      var projected = this.cartSubtotal + pricing.ctaTotal;
      var unlocked = projected >= this.freeShippingThreshold;

      return {
        popup_session_id: this.popupSessionId,
        source_surface: this.sourceSurface,
        source_template: this.sourceTemplate,
        device_type: this.deviceType,
        product_id: String(product.id),
        product_handle: product.handle,
        product_title: product.title,
        variant_id: String(variant.id),
        variant_title: variant.title,
        variant_tag: normalizeVariantTag(variant.badge),
        unit_price: pricing.basePrice / 100,
        effective_unit_price: effectiveUnitPrice / 100,
        quantity: quantity,
        line_total: pricing.ctaTotal / 100,
        currency: this.currency,
        subscription_enabled: this.state.subscribe,
        subscription_frequency: this.state.subscribe ? frequency.name : null,
        subscription_discount_pct: formatPercent(pricing.discountRate),
        cart_total_before: this.cartSubtotal / 100,
        free_shipping_threshold: this.freeShippingThreshold / 100,
        free_shipping_remaining: Math.max(0, (this.freeShippingThreshold - projected) / 100),
        free_shipping_unlocked: unlocked
      };
    }

    trackOpen() {
      var product = this.product;
      var hasSubscriptionPlan = !!(product.sellingPlanGroups[0] && product.sellingPlanGroups[0].sellingPlans.length);
      pushDataLayerEvent('atc_popup_open', Object.assign(this.buildAnalyticsContext(), {
        default_variant_id: String(this.currentVariant().id),
        variant_count: product.variants.length,
        has_subscription_plan: hasSubscriptionPlan
      }));
    }

    trackVariantChange(previousVariant, newVariant) {
      this._variantChangeIndex += 1;
      this._interactionCount += 1;
      pushDataLayerEvent('atc_popup_variant_change', Object.assign(this.buildAnalyticsContext(), {
        previous_variant_id: String(previousVariant.id),
        previous_variant_title: previousVariant.title,
        previous_unit_price: previousVariant.price / 100,
        new_variant_id: String(newVariant.id),
        change_index: this._variantChangeIndex
      }));
    }

    // Debounced 500ms so holding +/- produces one event with the final
    // value, not one per tap (BRD event 3).
    trackQuantityChangeDebounced(direction) {
      var self = this;
      clearTimeout(this._qtyDebounceTimer);
      this._qtyDebounceTimer = setTimeout(function () {
        var baseline = self._qtyBaseline;
        self._qtyBaseline = null;
        if (baseline === null || baseline === self.state.quantity) return;
        self._interactionCount += 1;
        pushDataLayerEvent('atc_popup_quantity_change', Object.assign(self.buildAnalyticsContext(), {
          previous_quantity: baseline,
          new_quantity: self.state.quantity,
          direction: direction
        }));
      }, 500);
    }

    trackSubscriptionToggle(beforePricing, afterPricing) {
      this._interactionCount += 1;
      pushDataLayerEvent('atc_popup_subscription_toggle', Object.assign(this.buildAnalyticsContext(), {
        new_state: this.state.subscribe ? 'on' : 'off',
        line_total_before: beforePricing.ctaTotal / 100,
        line_total_after: afterPricing.ctaTotal / 100
      }));
    }

    trackFrequencyChange(previousFrequency, newFrequency) {
      this._interactionCount += 1;
      pushDataLayerEvent('atc_popup_frequency_change', Object.assign(this.buildAnalyticsContext(), {
        previous_frequency: previousFrequency.name,
        new_frequency: newFrequency.name
      }));
    }

    trackShippingStateChange(unlocked) {
      this._interactionCount += 1;
      pushDataLayerEvent('atc_popup_shipping_state_change', Object.assign(this.buildAnalyticsContext(), {
        new_state: unlocked ? 'unlocked' : 'locked',
        triggered_by: this._shippingChangeTrigger || 'variant_change'
      }));
    }

    // Standard GA4 ecommerce schema (BRD 11.4) - the only event that keeps
    // its unprefixed name so existing GA4 funnel/revenue reports keep working.
    trackAddToCart(variant, frequency) {
      var context = this.buildAnalyticsContext();
      var pricing = computePricing(this.product, variant, this.state);
      var discountPerUnit = this.state.subscribe ? (pricing.unitPrice - Math.round(pricing.unitPrice * (1 - pricing.discountRate))) : 0;

      pushDataLayerEvent('add_to_cart', {
        popup_session_id: context.popup_session_id,
        source_surface: context.source_surface,
        add_method: 'atc_popup',
        ecommerce: {
          currency: context.currency,
          value: pricing.ctaTotal / 100,
          items: [{
            item_id: String(variant.id),
            item_name: this.product.title,
            item_variant: variant.title,
            item_brand: this.product.vendor || '',
            item_category: this.product.type || '',
            price: pricing.unitPrice / 100,
            discount: discountPerUnit / 100,
            quantity: this.state.quantity
          }]
        },
        subscription_enabled: context.subscription_enabled,
        subscription_frequency: context.subscription_frequency,
        subscription_discount_pct: context.subscription_discount_pct,
        selling_plan_id: (this.state.subscribe && frequency.id) ? frequency.id : null,
        free_shipping_unlocked: context.free_shipping_unlocked,
        free_shipping_remaining: context.free_shipping_remaining,
        cart_total_before: context.cart_total_before
      });
    }

    trackAddError(err) {
      this._addAttempts += 1;
      this._interactionCount += 1;
      pushDataLayerEvent('atc_popup_add_error', Object.assign(this.buildAnalyticsContext(), {
        error_code: (err && err.code) || 'add_to_cart_failed',
        error_message: (err && err.message) || 'Unknown error',
        attempt_number: this._addAttempts
      }));
    }

    trackViewDetailsClick(clickElement) {
      this._interactionCount += 1;
      pushDataLayerEvent('atc_popup_view_details_click', Object.assign(this.buildAnalyticsContext(), {
        click_element: clickElement,
        variant_at_click: String(this.currentVariant().id)
      }));
    }

    trackClose(reason) {
      var timeOpenMs = this._openedAt ? (Date.now() - this._openedAt) : 0;
      pushDataLayerEvent('atc_popup_close', Object.assign(this.buildAnalyticsContext(), {
        close_method: reason,
        added_to_cart: !!this._addedToCart,
        time_open_ms: timeOpenMs,
        interaction_count: this._interactionCount || 0
      }));
    }
  }

  function escapeHtml(str) {
    return String(str == null ? '' : str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  window.customElements.define('quick-add-modal', QuickAddModalElement);
})();
